import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Compact "known figure" chip linking to the figure page. Reused wherever a
 * wallet resolves to a figure (token creator, top holders, holder table).
 */
interface Props {
  figureKey: string;
  displayName: string;
  trustLevel?: string | null;  // accepted for back-compat; trust is internal-only, not shown
  confidence?: 'confirmed' | 'probable';  // 'probable' = agent-linkage lead, not settled fact
  percentage?: number;      // when shown as a holder, their share of supply
  sent?: boolean;           // the position was SENT to them (unsigned airdrop/spoof), not acquired
  tokenAccount?: string;    // the holder's SPL token account for this mint — the % deep-links here on Solscan
  className?: string;
}

const FigureBadge: React.FC<Props> = ({ figureKey, displayName, confidence, percentage, sent, tokenAccount, className }) => {
  const probable = confidence === 'probable';
  // The name links to the figure; the % links to the on-chain token account, so
  // they need to be sibling anchors (an <a> can't nest inside the figure <Link>).
  const pctText = typeof percentage === 'number' && percentage > 0
    ? (sent ? `sent ${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`)
    : null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? 'text-sm'}`}>
      <Link
        to={`/figures/${figureKey}`}
        onClick={(e) => e.stopPropagation()}
        title={(probable
          ? `Probable identity (unconfirmed lead) — ${displayName}`
          : `Known figure: ${displayName}`)
          + (sent ? ' · supply was SENT to this wallet (airdrop/spoof), not acquired' : '')}
        className={`inline-flex items-center gap-1.5 hover:underline font-semibold ${probable ? 'text-amber-400' : 'text-crypto-accent'}`}
      >
        <i className={`fas ${probable ? 'fa-user-tag text-amber-400' : 'fa-user-check text-crypto-green'} text-xs`} />
        <span>{displayName}</span>
        {probable && <span className="text-amber-400/70 text-[10px] font-normal">?</span>}
      </Link>
      {pctText && (
        tokenAccount ? (
          <a
            href={`https://solscan.io/account/${tokenAccount}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`View this holding's token account on Solscan${sent ? ' (supply was sent here, not acquired)' : ''}`}
            className="text-gray-500 font-normal hover:underline hover:text-gray-300"
          >
            {pctText}
          </a>
        ) : (
          <span className="text-gray-500 font-normal">{pctText}</span>
        )
      )}
    </span>
  );
};

export default FigureBadge;
